import type { RescheduleContextDto } from '@shared/core/reschedule'
import { formatInTimeZone } from 'date-fns-tz'
import {
  computeEndTimeDisplay,
  getTimeRangeLabel,
} from 'src/modules/booking/components/booking-confirmed-step/booking-confirmed-step.domain'
import type {
  AppointmentTypeForConfirmed,
  BookingConfirmedDisplayData,
} from 'src/modules/booking/components/booking-confirmed-step/booking-confirmed-step.interface'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isBookingIdUuid(identifier: string): boolean {
  return UUID_REGEX.test(identifier)
}

// /v1/bookings/:bookingId rejects a non-uuid with a 400, and a booking-sourced
// reschedule context can still carry a url_code as the identifier.
export function shouldLoadBookingForCalendar(
  identifier: string,
  context: RescheduleContextDto | null | undefined,
): boolean {
  if (!identifier || !isBookingIdUuid(identifier)) return false
  return context ? context.source === 'booking' : true
}

export function getDisplayDataFromRescheduleContext(context: RescheduleContextDto): BookingConfirmedDisplayData {
  if (!context.start_time_utc || !context.initialData?.timezone) {
    return {
      selectedDate: null,
      selectedTime: null,
      endTime: null,
      timezone: null,
      appointmentType: context.selected_appointment_type_id ?? null,
    }
  }
  const tz = context.initialData.timezone
  const startDate = new Date(context.start_time_utc)
  const selectedTime = formatInTimeZone(startDate, tz, 'h:mm a')
  const endTime = context.end_time_utc ? formatInTimeZone(new Date(context.end_time_utc), tz, 'h:mm a') : null
  return {
    selectedDate: startDate,
    selectedTime,
    endTime,
    timezone: tz,
    appointmentType: context.selected_appointment_type_id ?? null,
  }
}

export function getProviderDisplayName(providerDetails: { full_name?: string } | null | undefined): string {
  return providerDetails?.full_name ?? 'Provider'
}

export function getTimeRangeLabelForDisplay(
  displayData: BookingConfirmedDisplayData,
  appointmentType: AppointmentTypeForConfirmed | null,
): string {
  if (!displayData.selectedTime) return ''
  const durationMinutes = appointmentType?.duration_minutes ?? 30
  const endTime = displayData.endTime ?? computeEndTimeDisplay(displayData.selectedTime, durationMinutes)
  return getTimeRangeLabel(displayData.selectedTime, endTime, displayData.timezone)
}

export function shouldShowAppointmentDetails(
  displayData: BookingConfirmedDisplayData,
  appointmentType: AppointmentTypeForConfirmed | null,
  providerName: string,
): boolean {
  return !!(displayData.selectedDate && displayData.selectedTime && (appointmentType || providerName))
}

/** Single check for "appointment is in the future" using end_time_utc (string or Date). Use this for both booking and context. */
export function isAppointmentEndInTheFuture(endTimeUtc: string | Date | null | undefined): boolean {
  if (endTimeUtc == null) return false
  return new Date(endTimeUtc) > new Date()
}
